import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { useUpdateMerchantSettings } from '@/api/domains/merchant-settings';
import { HIGHLIGHT_RING, useHighlightSection } from '@/hooks/use-highlight-section';
import { toApiProblem, toastProblem } from '@/lib/errors';
import { cn } from '@/lib/utils';

import { SettingsCard, SettingsSection, SettingsToggleRow } from '../components/SettingsSection';

interface TestModeSectionProps {
  enabled: boolean;
}

/**
 * Merchant test mode: while on, InPost rates at checkout are visible only to
 * buyers whose shipping last name is "Test" (the marker is hardcoded backend-side
 * in AggregateRateQuotes), so the merchant can vet the app on a live store
 * without exposing rates to real buyers. Saves immediately on toggle.
 */
export function TestModeSection({ enabled }: TestModeSectionProps) {
  const { t } = useTranslation('settings');
  const highlight = useHighlightSection<HTMLElement>('test-mode');

  const update = useUpdateMerchantSettings();

  const [pending, setPending] = useState<boolean | null>(null);
  const checked = pending ?? enabled;

  useEffect(() => {
    if (pending !== null && pending === enabled) {
      setPending(null);
    }
  }, [pending, enabled]);

  const handleToggle = (next: boolean) => {
    if (update.isPending) return;
    setPending(next);
    update.mutate(
      { settings: { test_mode_enabled: next } },
      {
        onSuccess: () => toast.success(t('saved')),
        onError: (error) => {
          setPending(null);
          toastProblem(toApiProblem(error));
        },
      },
    );
  };

  return (
    <SettingsSection
      ref={highlight.ref}
      title={t('checkout.testMode.title')}
      description={t('checkout.testMode.description')}
      className={cn(highlight.isHighlighted && HIGHLIGHT_RING)}
    >
      <SettingsCard>
        <SettingsToggleRow
          label={t('checkout.testMode.label')}
          description={t('checkout.testMode.toggleDescription')}
          checked={checked}
          onCheckedChange={handleToggle}
        >
          {checked ? (
            <div className="rounded-[9px] border border-[#f0e6bb] bg-[#fdf6da] px-3 py-2.5 text-[13px] text-[#6b5a10]">
              {t('checkout.testMode.note')}
            </div>
          ) : null}
        </SettingsToggleRow>
      </SettingsCard>
    </SettingsSection>
  );
}
